import React, { useEffect, useState } from 'react';
import { TableRow, Box, Grid2 } from '@mui/material';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';

import { CSS } from '@dnd-kit/utilities';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import DragIcon from '@assets/images/drag-icon.svg';
import DeleteIcon from '@assets/images/delete_table.svg';
import CustomAutoCompleteSingleSelection from '@ui-components/AutoComplete/CustomAutoCompleteSingleSelection';
import type { Option } from '@ui-components/AutoComplete/CustomAutoCompleteSingleSelection';
import type { FacilityFields as FacilityFieldType } from '../../API/services/IntakeFormService';

export interface FacilityFieldRowData {
  id: number;
  name: string;
}

interface FacilityFieldsProps {
  facilityFields: FacilityFieldRowData[];
  setFacilityFields: React.Dispatch<
    React.SetStateAction<FacilityFieldRowData[]>
  >;
  facilityFieldsddl: FacilityFieldType[];
}

const SortableRow: React.FC<{
  row: FacilityFieldRowData;
  onDelete: (id: number) => void;
}> = ({ row, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes}>
      <CustomTableCell
        id="drag-handle-cell"
        {...attributes}
        {...listeners}
        className="width-5"
      >
        <CustomIconButton id="drag-icon" size="small" icon={<DragIcon />} />
      </CustomTableCell>
      <CustomTableCell id="field-label-cell">
        <CustomTypography id="field-label-cell-text" variant="body3">
          {row.name}
        </CustomTypography>
      </CustomTableCell>
      <CustomTableCell id="delete-button-cell" className="width-10">
        <CustomIconButton
          id="delete-icon"
          size="small"
          icon={<DeleteIcon />}
          onClick={() => onDelete(row.id)}
        />
      </CustomTableCell>
    </TableRow>
  );
};

const FacilityFields: React.FC<FacilityFieldsProps> = ({
  facilityFields,
  setFacilityFields,
  facilityFieldsddl,
}) => {
  const [facilityFieldOptions, setFacilityFieldOptions] = useState<
    FacilityFieldType[]
  >([]);

  useEffect(() => {
    if (facilityFieldsddl.length > 0) {
      const selectedIds = new Set(facilityFields.map((f) => f.id));

      const sortedData = facilityFieldsddl.filter(
        (item) => !selectedIds.has(item.id)
      );

      setFacilityFieldOptions(sortedData);
    }
  }, [facilityFieldsddl, facilityFields]);

  const [selectedFacilityFields, setSelectedFacilityFields] =
    useState<Option | null>(null);

  const sensors = useSensors(useSensor(PointerSensor));
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = facilityFields.findIndex((r) => r.id === active.id);
    const newIndex = facilityFields.findIndex((r) => r.id === over.id);
    setFacilityFields((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const handleDelete = (id: number) => {
    const deletedRow = facilityFields.find((row) => row.id === id);
    setFacilityFields((prev) => prev.filter((row) => row.id !== id));
    if (deletedRow) {
      setFacilityFieldOptions((prev) =>
        [...prev, { id: deletedRow.id, name: deletedRow.name }].sort((a, b) =>
          a.name.localeCompare(b.name)
        )
      );
    }
  };

  const handleFacilityFieldsSelect = (val: Option | null) => {
    if (!val) return;

    const id = typeof val.id === 'string' ? Number(val.id) : val.id;

    // Clear selection first
    setSelectedFacilityFields(null);

    setFacilityFields((prev) => [...prev, { id, name: val.name }]);

    // Remove selected option from dropdown
    setFacilityFieldOptions((prev) =>
      prev.filter((item) => item.id !== val.id)
    );
  };

  return (
    <Box>
      <Box className="pb-20">
        <Grid2 container>
          <Grid2
            size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
            className="flex align-items-center"
          >
            <Box>
              <CustomTypography id="session-fields-title" variant="h5">
                Facility Fields
              </CustomTypography>
            </Box>
          </Grid2>
          <Grid2
            size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
            className="align-items-center text-align-right"
          >
            <Box>
              <CustomAutoCompleteSingleSelection
                key={facilityFieldOptions.length}
                placeholder="Search facility fields"
                options={facilityFieldOptions}
                value={selectedFacilityFields}
                onChange={handleFacilityFieldsSelect}
                size="small"
                noOptionsText="No Facility Fields Found"
              />
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box>
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
              <SortableContext
                items={facilityFields.map((r) => r.id)}
                strategy={verticalListSortingStrategy}
              >
                <CustomTable id="session-fields-table">
                  <CustomTableHead id="session-fields-table-head">
                    <CustomTableRow id="session-fields-table-header-row">
                      <CustomTableCell
                        id="session-fields-sorting-column"
                        className="width-5"
                      ></CustomTableCell>
                      <CustomTableCell id="fecility-field-name-column">
                        FIELD NAME
                      </CustomTableCell>
                      <CustomTableCell id="facility-action-column">
                        ACTIONS
                      </CustomTableCell>
                    </CustomTableRow>
                  </CustomTableHead>
                  {facilityFields.length > 0 ? (
                    <CustomTableBody id="session-fields-table-body">
                      {facilityFields.map((row) => (
                        <SortableRow
                          key={row.id}
                          row={row}
                          onDelete={handleDelete}
                        />
                      ))}
                    </CustomTableBody>
                  ) : (
                    <CustomTableBody id="session-fields-table-body">
                      <CustomTableRow id="no-data-row">
                        <CustomTableCell id="no-data-cell" colspan={3}>
                          <Box className="flex flex-justify-center align-items-center">
                            <CustomTypography
                              id="no-data-cell-text"
                              variant="body3"
                              color="textDisabled"
                            >
                              No Facility Fields Added
                            </CustomTypography>
                          </Box>
                        </CustomTableCell>
                      </CustomTableRow>
                    </CustomTableBody>
                  )}
                </CustomTable>
              </SortableContext>
            </DndContext>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default FacilityFields;
